import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import React, { useEffect, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getEndpointShieldHistory,
  IEndpointBlockedRow,
  IEndpointShieldHistoryItem,
  IEndpointShieldHistoryResponse,
} from '~/src/dash/api/endpoint-shield';

// Neo-Brutalism Light Mode Color Palette
const colors = {
  background: '#F8F9FA',
  cardBackground: '#FFFFFF',
  accentSalmon: '#FFA69E',
  accentPeriwinkle: '#B8B8FF',
  accentYellow: '#FEE72F',
  accentMint: '#98F5E1',
  textPrimary: '#1A1A2E',
  textSecondary: '#4A4A5A',
  border: '#E0E0E0',
  shadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  triggered: '#FF6B6B',
  notTriggered: '#4ECDC4',
  impressions: '#6366F1',
  requests: '#F59E0B',
  throttle: '#EF4444',
  blocked: '#DC2626',
};

const useStyles = makeStyles({
  container: {
    backgroundColor: colors.background,
    padding: '24px',
    minHeight: '400px',
    borderRadius: '12px',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 800,
    fontSize: '28px',
    textTransform: 'uppercase',
    color: colors.textPrimary,
    letterSpacing: '1px',
  },
  subtitle: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    color: colors.textSecondary,
    marginTop: '4px',
  },
  infoBox: {
    backgroundColor: '#FFF9E6',
    border: `2px solid ${colors.accentYellow}`,
    borderRadius: '12px',
    padding: '12px 16px',
    marginTop: '16px',
  },
  infoBoxText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 500,
    fontSize: '13px',
    color: colors.textSecondary,
    lineHeight: 1.6,
    margin: 0,
    '& li': {
      marginBottom: '4px',
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
  bentoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCardMint: {
    borderLeft: `6px solid ${colors.accentMint}`,
  },
  statCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: colors.shadow,
    border: `2px solid ${colors.border}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  statCardSalmon: {
    borderLeft: `6px solid ${colors.accentSalmon}`,
  },
  statCardPeriwinkle: {
    borderLeft: `6px solid ${colors.accentPeriwinkle}`,
  },
  statCardYellow: {
    borderLeft: `6px solid ${colors.accentYellow}`,
  },
  statLabel: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    color: colors.textSecondary,
    letterSpacing: '1.5px',
    marginBottom: '8px',
  },
  statValue: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 800,
    fontSize: '32px',
    color: colors.textPrimary,
  },
  accordion: {
    backgroundColor: colors.cardBackground,
    borderRadius: '12px !important',
    boxShadow: colors.shadow,
    border: `2px solid ${colors.border}`,
    marginBottom: '12px',
    overflow: 'hidden',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 12px 0',
    },
  },
  accordionSummary: {
    backgroundColor: colors.background,
    borderBottom: `2px solid ${colors.border}`,
    minHeight: '64px',
    '&.Mui-expanded': {
      minHeight: '64px',
    },
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
  accordionDetails: {
    padding: '24px',
    backgroundColor: colors.cardBackground,
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  chip: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    backgroundColor: colors.cardBackground,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    height: '32px',
    '& .MuiChip-label': {
      paddingLeft: '12px',
      paddingRight: '4px',
    },
  },
  chipCountry: {
    borderColor: colors.accentPeriwinkle,
    backgroundColor: '#F0F0FF',
  },
  chipAppBundle: {
    borderColor: colors.accentMint,
    backgroundColor: '#F0FFF8',
  },
  copyButton: {
    padding: '2px',
    marginLeft: '-4px',
    marginRight: '4px',
    '& svg': {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '20px',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeTriggered: {
    backgroundColor: '#FFE5E5',
    color: colors.triggered,
    border: `2px solid ${colors.triggered}`,
  },
  badgeNotTriggered: {
    backgroundColor: '#E5FFF9',
    color: colors.notTriggered,
    border: `2px solid ${colors.notTriggered}`,
  },
  chartContainer: {
    width: '100%',
    height: '350px',
  },
  summaryStats: {
    display: 'flex',
    gap: '24px',
    marginLeft: 'auto',
    marginRight: '16px',
  },
  summaryStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryStatLabel: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '10px',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryStatValue: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    color: colors.textPrimary,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    gap: '16px',
  },
  loadingText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  loadingSubtext: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 400,
    fontSize: '13px',
    color: colors.textSecondary,
    textAlign: 'center' as const,
    maxWidth: '400px',
    lineHeight: 1.5,
  },
  progressBarContainer: {
    width: '100%',
    maxWidth: '400px',
    marginTop: '8px',
    position: 'relative' as const,
    overflow: 'visible',
  },
  progressBarTrack: {
    height: '8px',
    borderRadius: '4px',
    backgroundColor: colors.border,
    width: '100%',
    position: 'relative' as const,
    overflow: 'visible',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '4px',
    backgroundColor: colors.accentPeriwinkle,
    position: 'absolute' as const,
    left: 0,
    top: 0,
    transition: 'width 0.1s linear',
  },
  progressPercentage: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '12px',
    color: colors.accentPeriwinkle,
    marginTop: '8px',
  },
  errorContainer: {
    backgroundColor: '#FFE5E5',
    border: `2px solid ${colors.triggered}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
  },
  errorText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: colors.triggered,
  },
  emptyContainer: {
    backgroundColor: colors.cardBackground,
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
  },
  emptyText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '16px',
    color: colors.textSecondary,
  },
  emptySubtext: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: colors.textSecondary,
    marginTop: '8px',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: colors.cardBackground,
    borderRadius: '12px',
    border: `2px solid ${colors.border}`,
  },
  paginationInfo: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: colors.textSecondary,
  },
  paginationButtons: {
    display: 'flex',
    gap: '8px',
  },
  paginationButton: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    padding: '8px 16px',
    borderRadius: '8px',
    border: `2px solid ${colors.border}`,
    backgroundColor: colors.cardBackground,
    '&:hover': {
      backgroundColor: colors.background,
    },
    '&:disabled': {
      opacity: 0.5,
    },
  },
  searchContainer: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  searchInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: colors.cardBackground,
      fontFamily: '"Inter", "Roboto", sans-serif',
    },
  },
  sortSwitchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.cardBackground,
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '4px 12px',
    whiteSpace: 'nowrap' as const,
  },
  sortLabel: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '12px',
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  sortLabelActive: {
    color: colors.textPrimary,
    fontWeight: 700,
  },
  gatedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    textAlign: 'center' as const,
  },
  gatedTitle: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 800,
    fontSize: '24px',
    textTransform: 'uppercase' as const,
    color: colors.textPrimary,
    letterSpacing: '1px',
    marginBottom: '16px',
  },
  gatedSubtitle: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    color: colors.textSecondary,
    marginBottom: '32px',
  },
  gatedButton: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'none' as const,
    padding: '12px 24px',
    borderRadius: '12px',
    border: `2px solid ${colors.accentPeriwinkle}`,
    backgroundColor: 'transparent',
    color: colors.accentPeriwinkle,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: colors.accentPeriwinkle,
      color: '#FFFFFF',
    },
  },
});

interface IEndpointShieldViewProps {
  siteId: string;
  publisherId: string;
  isReseller: boolean;
}

interface IGroupedData {
  hash: string;
  country: string;
  appBundle: string;
  hasTriggered: boolean;
  totalRequests: number;
  totalImpressions: number;
  currentThrottle: number;
  yesterdayRequests: number;
  yesterdayBlocked: number;
  todayBlocked: number;
  chartData: IChartDataPoint[];
}

interface IChartDataPoint {
  date: string;
  impressions: number | null;
  endpointRequests: number | null;
  throttlePercentage: number;
  totalBlocked: number;
}

export const EndpointShieldView: React.FC<IEndpointShieldViewProps> = ({
  siteId,
  publisherId,
  isReseller,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<IEndpointShieldHistoryResponse | null>(null);
  const [groupedData, setGroupedData] = useState<IGroupedData[]>([]);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState<number>(0);
  const [sortByBlocked, setSortByBlocked] = useState<boolean>(false);
  const [showAnywayContent, setShowAnywayContent] = useState<boolean>(false);

  const ITEMS_PER_PAGE = 20;

  const LOADING_MESSAGES = [
    'Fetching shield data from analytics...',
    'Processing endpoint activity logs...',
    'Analyzing blocking patterns...',
    'Aggregating country and app bundle data...',
    'Almost there, hang tight...',
  ];

  const handleCopy = (text: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopySnackbarOpen(true);
  };

  // Loading progress animation (~40 seconds)
  useEffect(() => {
    if (!loading) {
      setLoadingProgress(0);
      setLoadingMessageIndex(0);
      return;
    }

    // Progress bar animation - reaches ~100% in 40 seconds, then keeps going as a joke
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        // After 100%, slow down but keep going as a joke
        if (prev >= 100) return prev + 0.125;
        return prev + 1;
      });
    }, 400); // 400ms * 100 steps = 40 seconds to reach 100%

    // Rotate messages every 2 seconds
    const messageInterval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [loading]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const [response, err] = await getEndpointShieldHistory(siteId, publisherId);

      if (err) {
        setError(err.message || 'Failed to fetch endpoint shield history');
        setLoading(false);
        return;
      }

      if (response) {
        setData(response);
        const grouped = groupDataByHash(response.data, response.blocked_data || []);
        setGroupedData(grouped);
        // Expand first panel by default if there's data
        if (grouped.length > 0) {
          setExpandedPanels([grouped[0].hash]);
        }
      }
      setLoading(false);
    };

    if (siteId) {
      fetchData();
    }
  }, [siteId, publisherId]);

  const groupDataByHash = (
    items: IEndpointShieldHistoryItem[],
    blockedData: IEndpointBlockedRow[] = [],
  ): IGroupedData[] => {
    const hashMap = new Map<string, IEndpointShieldHistoryItem[]>();

    // Group items by hash
    items.forEach((item) => {
      const existing = hashMap.get(item.hash) || [];
      existing.push(item);
      hashMap.set(item.hash, existing);
    });

    // Create a map for blocked data: hash+dateKey -> { totalBlocked, endpointRequests, impressions }
    interface IBlockedDataEntry {
      totalBlocked: number;
      endpointRequests: number;
      impressions: number;
    }
    const blockedMap = new Map<string, IBlockedDataEntry>();
    blockedData.forEach((blocked) => {
      const date = new Date(blocked.day);
      const dateKey = `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
      const key = `${blocked.hash}-${dateKey}`;
      // Sum up values for same hash+date (in case of multiple blocked codes)
      const existing = blockedMap.get(key) || { totalBlocked: 0, endpointRequests: 0, impressions: 0 };
      blockedMap.set(key, {
        totalBlocked: existing.totalBlocked + (blocked.total_blocked ?? 0),
        endpointRequests: existing.endpointRequests + (blocked.endpoint_requests ?? 0),
        impressions: existing.impressions + (blocked.impressions ?? 0),
      });
    });

    // Convert to grouped data array
    const result: IGroupedData[] = [];

    hashMap.forEach((hashItems, hash) => {
      // Sort items by date
      const sortedItems = hashItems.sort(
        (a, b) => new Date(a.ts_date).getTime() - new Date(b.ts_date).getTime(),
      );

      const firstItem = sortedItems[0];
      const lastItem = sortedItems[sortedItems.length - 1];
      const hasTriggered = sortedItems.some((item) => item.triggered);
      const totalRequests = sortedItems.reduce((sum, item) => sum + item.endpoint_requests, 0);
      const totalImpressions = sortedItems.reduce((sum, item) => sum + item.impressions, 0);
      const currentThrottle = lastItem.throttle_percentage;

      // Create a map of date string to item for quick lookup
      const dateToItemMap = new Map<string, IEndpointShieldHistoryItem>();
      sortedItems.forEach((item) => {
        const date = new Date(item.ts_date);
        const dateKey = `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
        dateToItemMap.set(dateKey, item);
      });

      // Generate 7 days of data starting from today going backwards
      const chartData: IChartDataPoint[] = [];
      const today = new Date();

      // Calculate yesterday's date key for sorting
      const yesterday = new Date(today);
      yesterday.setUTCDate(today.getUTCDate() - 1);
      const yesterdaySortKey = `${yesterday.getUTCFullYear()}-${yesterday.getUTCMonth() + 1}-${yesterday.getUTCDate()}`;
      const yesterdaySortItem = dateToItemMap.get(yesterdaySortKey);
      const yesterdayRequests = yesterdaySortItem?.endpoint_requests ?? 0;

      // Get yesterday's blocked count
      const yesterdayBlockedKey = `${hash}-${yesterdaySortKey}`;
      const yesterdayBlockedData = blockedMap.get(yesterdayBlockedKey);
      const yesterdayBlocked = yesterdayBlockedData?.totalBlocked ?? 0;

      // Get today's blocked count and real-time data
      const todaySortKey = `${today.getUTCFullYear()}-${today.getUTCMonth() + 1}-${today.getUTCDate()}`;
      const todayBlockedKey = `${hash}-${todaySortKey}`;
      const todayBlockedData = blockedMap.get(todayBlockedKey);
      const todayBlocked = todayBlockedData?.totalBlocked ?? 0;

      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setUTCDate(today.getUTCDate() - i);
        const dateKey = `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
        const displayDate = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;

        // Check if this is today (i === 0 means today)
        const isToday = i === 0;

        // Get the item for this date (for impressions and requests from daily data)
        const item = dateToItemMap.get(dateKey);

        // Get blocked data for this hash+date (includes real-time req/imps for today)
        const blockedKey = `${hash}-${dateKey}`;
        const blockedDataEntry = blockedMap.get(blockedKey);
        const totalBlocked = blockedDataEntry?.totalBlocked ?? 0;

        // For "Allowed %", we need yesterday's value (shifted forward by 1 day)
        // So for today's display, we use yesterday's throttle percentage
        const yesterdayDate = new Date(date);
        yesterdayDate.setUTCDate(date.getUTCDate() - 1);
        const yesterdayKey = `${yesterdayDate.getUTCFullYear()}-${yesterdayDate.getUTCMonth() + 1}-${yesterdayDate.getUTCDate()}`;
        const yesterdayItem = dateToItemMap.get(yesterdayKey);

        // For today, use real-time data from blocked_data; for other days use daily data
        let impressionsValue: number | null;
        let endpointRequestsValue: number | null;

        if (isToday) {
          // Use real-time data from blocked_data for today
          impressionsValue = blockedDataEntry?.impressions ?? null;
          endpointRequestsValue = blockedDataEntry?.endpointRequests ?? null;
        } else {
          // Use daily data for past days
          impressionsValue = item?.impressions ?? 0;
          endpointRequestsValue = item?.endpoint_requests ?? 0;
        }

        chartData.push({
          date: displayDate,
          impressions: impressionsValue,
          endpointRequests: endpointRequestsValue,
          // Use yesterday's throttle percentage for today's display
          // Default to 100% allowed when no throttle data exists
          throttlePercentage: yesterdayItem?.throttle_percentage ?? 100,
          totalBlocked,
        });
      }

      result.push({
        hash,
        country: firstItem.country,
        appBundle: firstItem.app_bundle,
        hasTriggered,
        totalRequests,
        totalImpressions,
        currentThrottle,
        yesterdayRequests,
        yesterdayBlocked,
        todayBlocked,
        chartData,
      });
    });

    // Sort by yesterday's requests (highest on top)
    return result.sort((a, b) => b.yesterdayRequests - a.yesterdayRequests);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const handleAccordionChange = (hash: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpandedPanels((prev) =>
      isExpanded ? [...prev, hash] : prev.filter((h) => h !== hash),
    );
  };

  const calculateOverallStats = () => {
    const uniqueHashes = groupedData.length;
    const triggeredCount = groupedData.filter((g) => g.hasTriggered).length;
    const totalRequestsYesterday = groupedData.reduce((sum, g) => sum + g.yesterdayRequests, 0);
    const totalBlockedToday = groupedData.reduce((sum, g) => sum + g.todayBlocked, 0);

    return { uniqueHashes, triggeredCount, totalRequestsYesterday, totalBlockedToday };
  };

  // Show gated screen for non-resellers unless they clicked "show anyway"
  if (!isReseller && !showAnywayContent) {
    return (
      <Grid className={classes.container}>
        <div className={classes.gatedContainer}>
          <Typography className={classes.gatedTitle}>
            Endpoint Shield only available for Resellers (for now)
          </Typography>
          <Typography className={classes.gatedSubtitle}>
            This feature is currently limited to reseller publishers.
          </Typography>
          <button
            className={classes.gatedButton}
            onClick={() => setShowAnywayContent(true)}
          >
            Click here to see how it would look like for this channel anyways
          </button>
        </div>
      </Grid>
    );
  }

  if (loading) {
    return (
      <Grid className={classes.container}>
        <div className={classes.loadingContainer}>
          <Typography className={classes.loadingText}>Loading Shield Data</Typography>
          <div className={classes.progressBarContainer}>
            <div className={classes.progressBarTrack}>
              <div
                className={classes.progressBarFill}
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
          </div>
          <Typography className={classes.progressPercentage}>{Math.round(loadingProgress)}%</Typography>
          <Typography className={classes.loadingSubtext}>
            {LOADING_MESSAGES[loadingMessageIndex]}
          </Typography>
          <Typography className={classes.loadingSubtext} style={{ marginTop: '8px', opacity: 0.7 }}>
            This may take a moment for channels with high traffic.
          </Typography>
        </div>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid className={classes.container}>
        <div className={classes.errorContainer}>
          <Typography className={classes.errorText}>{error}</Typography>
        </div>
      </Grid>
    );
  }

  if (!data || groupedData.length === 0) {
    return (
      <Grid className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>Endpoint Shield History</Typography>
          <Typography className={classes.subtitle}>Last 7 days of blocking activity</Typography>
        </div>
        <div className={classes.emptyContainer}>
          <Typography className={classes.emptyText}>No Shield Activity</Typography>
          <Typography className={classes.emptySubtext}>
            No endpoint shield blocking events recorded in the last 7 days for this channel.
          </Typography>
        </div>
      </Grid>
    );
  }

  const stats = calculateOverallStats();

  return (
    <Grid className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Endpoint Shield History</Typography>
        <Typography className={classes.subtitle}>
          {data.start_date} to {data.end_date}
        </Typography>
        <div className={classes.infoBox}>
          <ul className={classes.infoBoxText}>
            <li>Data refreshed hourly</li>
            <li>If yesterday impressions are 0, then we only allow 10% of the endpoint requests for today</li>
            <li>You might see blocked requests above 0 even when allowed is 100%; it's because the allow value is refreshed shortly after midnight</li>
            <li>ONLY ENABLED ON RESELLERS! (for now)</li>
          </ul>
        </div>
      </div>

      {/* Bento Grid Stats */}
      <div className={classes.bentoGrid}>
        <Paper className={`${classes.statCard} ${classes.statCardPeriwinkle}`} elevation={0}>
          <Typography className={classes.statLabel}>Unique Combinations</Typography>
          <Typography className={classes.statValue}>{stats.uniqueHashes}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardSalmon}`} elevation={0}>
          <Typography className={classes.statLabel}>Triggered</Typography>
          <Typography className={classes.statValue}>{stats.triggeredCount}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardYellow}`} elevation={0}>
          <Typography className={classes.statLabel}>Total Requests Yesterday</Typography>
          <Typography className={classes.statValue}>{formatNumber(stats.totalRequestsYesterday)}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardMint}`} elevation={0}>
          <Typography className={classes.statLabel}>Blocked Today</Typography>
          <Typography className={classes.statValue}>{formatNumber(stats.totalBlockedToday)}</Typography>
        </Paper>
      </div>

      {/* Search and Sort */}
      <div className={classes.searchContainer}>
        <TextField
          className={classes.searchInput}
          variant="outlined"
          size="small"
          placeholder="Search by country or app bundle..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0); // Reset to first page on search
          }}
        />
        <div className={classes.sortSwitchContainer}>
          <span className={`${classes.sortLabel} ${!sortByBlocked ? classes.sortLabelActive : ''}`}>
            Requests
          </span>
          <Switch
            checked={sortByBlocked}
            onChange={(e) => {
              setSortByBlocked(e.target.checked);
              setCurrentPage(0);
            }}
            color="default"
            size="small"
          />
          <span className={`${classes.sortLabel} ${sortByBlocked ? classes.sortLabelActive : ''}`}>
            Blocked
          </span>
        </div>
      </div>

      {/* Pagination */}
      {(() => {
        const filteredData = groupedData
          .filter(
            (group) =>
              group.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
              group.appBundle.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .sort((a, b) =>
            sortByBlocked
              ? b.todayBlocked - a.todayBlocked
              : b.yesterdayRequests - a.yesterdayRequests
          );
        const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
        const startIndex = currentPage * ITEMS_PER_PAGE;
        const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredData.length);
        const paginatedData = filteredData.slice(startIndex, endIndex);

        return (
          <>
            <div className={classes.paginationContainer}>
              <span className={classes.paginationInfo}>
                Showing {startIndex + 1}-{endIndex} of {filteredData.length} combinations
                {searchTerm && ` (filtered from ${groupedData.length})`}
              </span>
              <div className={classes.paginationButtons}>
                <Button
                  className={classes.paginationButton}
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  Previous
                </Button>
                <Button
                  className={classes.paginationButton}
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>

            {/* Grouped Accordions - Only render paginated items */}
            {paginatedData.map((group) => (
              <Accordion
                key={group.hash}
                className={classes.accordion}
                expanded={expandedPanels.includes(group.hash)}
                onChange={handleAccordionChange(group.hash)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.accordionSummary}
                  classes={{ content: classes.accordionSummaryContent }}
                >
                  <div className={classes.chipContainer}>
                    <Chip
                      className={`${classes.chip} ${classes.chipCountry}`}
                      label={group.country}
                      size="small"
                      deleteIcon={
                        <Tooltip title="Copy country">
                          <IconButton className={classes.copyButton} size="small">
                            <FileCopyOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      }
                      onDelete={handleCopy(group.country)}
                    />
                    <Chip
                      className={`${classes.chip} ${classes.chipAppBundle}`}
                      label={group.appBundle}
                size="small"
                deleteIcon={
                  <Tooltip title="Copy app bundle">
                    <IconButton className={classes.copyButton} size="small">
                      <FileCopyOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                }
                onDelete={handleCopy(group.appBundle)}
              />
            </div>
            <span
              className={`${classes.badge} ${
                group.hasTriggered ? classes.badgeTriggered : classes.badgeNotTriggered
              }`}
            >
              {group.hasTriggered ? 'Triggered' : 'Monitored'}
            </span>
            <div className={classes.summaryStats}>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Requests (Yesterday)</span>
                <span className={classes.summaryStatValue}>{formatNumber(group.yesterdayRequests)}</span>
              </div>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Blocked (Today)</span>
                <span className={classes.summaryStatValue}>{formatNumber(group.todayBlocked)}</span>
              </div>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Impressions</span>
                <span className={classes.summaryStatValue}>{formatNumber(group.totalImpressions)}</span>
              </div>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Allowed Requests</span>
                <span className={classes.summaryStatValue}>{group.currentThrottle}%</span>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {/* Chart - Only render when expanded for performance */}
            {expandedPanels.includes(group.hash) && (
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={group.chartData}
                    margin={{ top: 20, right: 60, left: 20, bottom: 20 }}
                  >
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: colors.textSecondary, fontWeight: 600 }}
                    axisLine={{ stroke: colors.border }}
                    tickLine={{ stroke: colors.border }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: colors.textSecondary, fontWeight: 600 }}
                    axisLine={{ stroke: colors.border }}
                    tickLine={{ stroke: colors.border }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: colors.throttle, fontWeight: 600 }}
                    axisLine={{ stroke: colors.throttle }}
                    tickLine={{ stroke: colors.throttle }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: colors.cardBackground,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '8px',
                      boxShadow: colors.shadow,
                    }}
                    labelStyle={{
                      fontWeight: 700,
                      color: colors.textPrimary,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                    }}
                    formatter={(value: number, name: string) => [
                      name === 'Allowed %' ? `${value}%` : formatNumber(value),
                      name,
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{
                      paddingTop: '16px',
                      fontFamily: '"Inter", "Roboto", sans-serif',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                    }}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="endpointRequests"
                    stroke={colors.requests}
                    fill={colors.requests}
                    fillOpacity={0.3}
                    strokeWidth={2}
                    dot={{ fill: colors.requests, strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                    name="Endpoint Requests"
                    isAnimationActive={false}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="totalBlocked"
                    stroke={colors.blocked}
                    fill={colors.blocked}
                    fillOpacity={0.3}
                    strokeWidth={2}
                    dot={{ fill: colors.blocked, strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                    name="Blocked"
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="impressions"
                    stroke={colors.impressions}
                    strokeWidth={3}
                    dot={{ fill: colors.impressions, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    name="Impressions"
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="throttlePercentage"
                    stroke={colors.throttle}
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ fill: colors.throttle, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    name="Allowed %"
                    isAnimationActive={false}
                  />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
            ))}
          </>
        );
      })()}

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
};
